import { motion, useScroll, useTransform } from "framer-motion";
import { StaticImageData } from "next/image";
import { useContext, useRef } from "react";
import { ResponsiveImage } from "~/components/ResponsiveImage";
import { ViewportContext } from "~/providers/ViewportProvider";

type StickyImageWrapperProps = {
  src: StaticImageData | string;
  alt: string;
  topOffset?: string;
  height?: string;
};

const StaticImageFrame = ({
  src,
  alt,
}: {
  src: StaticImageData | string;
  alt: string;
}) => (
  <div className="relative overflow-hidden rounded-sm shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)]">
    <ResponsiveImage
      src={src}
      alt={alt}
      placeholder={typeof src === "string" ? "empty" : "blur"}
      imageClassName="scale-[1.03] transition-transform duration-700"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-950/10 via-transparent to-white/20" />
  </div>
);

const DesktopAnimatedImage = ({
  src,
  alt,
  topOffset,
  height,
}: StickyImageWrapperProps) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, -24]);

  return (
    <div
      ref={imageRef}
      className="relative top-0 flex flex-col md:sticky md:self-start"
      style={{
        top: topOffset,
        height,
      }}
    >
      <motion.div
        className="relative overflow-hidden rounded-sm shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)]"
        style={{ y: imageY }}
        initial={{ scale: 1.06, opacity: 0.92, filter: "blur(10px)" }}
        whileInView={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <ResponsiveImage
          src={src}
          alt={alt}
          placeholder={typeof src === "string" ? "empty" : "blur"}
          imageClassName="scale-[1.03] transition-transform duration-700"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-950/10 via-transparent to-white/20" />
      </motion.div>
    </div>
  );
};

export const StickyImageWrapper = ({
  src,
  alt,
  topOffset = "100px",
  height = "400px",
}: StickyImageWrapperProps) => {
  const { isMobile } = useContext(ViewportContext);

  if (isMobile) {
    return (
      <div
        className="relative flex flex-col"
        style={{
          height,
        }}
      >
        <StaticImageFrame src={src} alt={alt} />
      </div>
    );
  }

  return (
    <DesktopAnimatedImage
      src={src}
      alt={alt}
      topOffset={topOffset}
      height={height}
    />
  );
};
